import { NotificationActionKind, type NotificationDto, NotificationSeverity } from "@/api/notifications";
import { ticketListViewPath } from "@/features/tickets/model/ticket-list-views";
import type { MessageValues, TranslationKey } from "@/locales/i18n";

type NotificationPresentation = {
  icon: string;
  iconClassName: string;
};

export function notificationPresentation(notification: NotificationDto): NotificationPresentation {
  if (notification.severity === NotificationSeverity.Critical) {
    return { icon: "tabler:alert-triangle", iconClassName: "border-destructive/50 text-destructive/80" };
  }
  if (notification.severity === NotificationSeverity.Warning) {
    return { icon: "tabler:alert-triangle", iconClassName: "border-warning/50 text-warning/80" };
  }

  switch (notification.type) {
    case "ticket.mentioned":
      return { icon: "tabler:at", iconClassName: "border-primary/40 text-primary/75" };
    case "ticket.participant_added":
      return { icon: "tabler:circle-check", iconClassName: "border-success/50 text-success/80" };
    case "ticket.reopened":
      return { icon: "tabler:refresh", iconClassName: "border-warning/50 text-warning/80" };
    case "ticket.assigned":
      return { icon: "tabler:user-check", iconClassName: "border-primary/40 text-primary/75" };
    default:
      return { icon: "tabler:info-circle", iconClassName: "border-primary/40 text-primary/75" };
  }
}

export function notificationCategoryLabel(notification: NotificationDto) {
  if (notification.aggregateType === "ticket" || notification.type.startsWith("ticket.")) {
    return "notifications.category.ticket" as const;
  }
  if (notification.type.startsWith("system.")) {
    return "notifications.category.system" as const;
  }
  return "notifications.category.default" as const;
}

type Translate = (key: TranslationKey, values?: MessageValues) => string;

function ticketNumberFromTitle(notification: NotificationDto): string | null {
  const pattern = notification.type === "ticket.assigned" || notification.type === "ticket.reopened" ? /^Ticket\s+(\S+)/i : /\bticket\s+(\S+)$/i;
  return notification.title.match(pattern)?.[1] ?? null;
}

export function localizedNotificationTitle(notification: NotificationDto, translate: Translate): string {
  const number = ticketNumberFromTitle(notification);
  switch (notification.type) {
    case "ticket.assigned":
      return number ? translate("notifications.ticketAssigned", { number }) : translate("notifications.ticketAssignedGeneric");
    case "ticket.mentioned":
      return number ? translate("notifications.ticketMentioned", { number }) : translate("notifications.ticketMentionedGeneric");
    case "ticket.participant_added":
      return number ? translate("notifications.ticketParticipantAdded", { number }) : translate("notifications.ticketParticipantAddedGeneric");
    case "ticket.reopened":
      return number ? translate("notifications.ticketReopened", { number }) : translate("notifications.ticketReopenedGeneric");
    default:
      return notification.title;
  }
}

export function localizedNotificationCategoryLabel(notification: NotificationDto, translate: Translate) {
  return translate(notificationCategoryLabel(notification));
}

export function notificationActionRoute(notification: NotificationDto): string | null {
  if (notification.isTargetAccessible && notification.actionKind === NotificationActionKind.TicketDetail && notification.actionTargetId) {
    return `${ticketListViewPath("all")}/${notification.actionTargetId}`;
  }
  return null;
}

export function formatNotificationTime(value: string, locale: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1_000));
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (seconds < 60) {
    return relative.format(0, "second");
  }
  if (seconds < 60 * 60) {
    return relative.format(-Math.floor(seconds / 60), "minute");
  }
  if (seconds < 24 * 60 * 60) {
    return relative.format(-Math.floor(seconds / (60 * 60)), "hour");
  }
  if (seconds < 7 * 24 * 60 * 60) {
    return relative.format(-Math.floor(seconds / (24 * 60 * 60)), "day");
  }

  return date.toLocaleString(locale, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
